import CheckIcon from '@mui/icons-material/Check';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadIcon from '@mui/icons-material/FileDownload';
import UploadIcon from '@mui/icons-material/FileUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Paper from '../../apiForm/panel/Paper';
import { downloadFile, readFile } from './downloadUtils';
import PricesIcon from '../../icons/tag.svg?react';
import ConfirmationDialog from '../../utilComponents/ConfirmationDialog';
import UploadDialog from './FileUpload/UploadDialog';
import useExportApi from './useExportApi';

export default function PricesExport() {
  const { t } = useTranslation();
  const api = useExportApi();
  const { projectId } = useParams();

  const [downloading, setDownloading] = useState(false);
  const [downloadedPricesCount, setDownloadedPricesCount] = useState<
    number | undefined
  >();

  const [uploading, setUploading] = useState(false);
  const [uploadingDialog, setUploadingDialog] = useState(false);
  const [uploadedPricesCount, setUploadedPricessCount] = useState<
    number | undefined
  >();

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const handlePricesDownload = useCallback(async () => {
    setDownloading(true);
    const data = await api.getPrices(null);
    if (!data) {
      setDownloadedPricesCount(0);
      setDownloading(false);
      return;
    }
    setDownloadedPricesCount(data.split('\n').length - 1);
    downloadFile(
      data,
      `${projectId}-prices-export.ndjson`,
      'application/x-ndjson',
    );
    setDownloading(false);
  }, [api, projectId]);

  const handlePricesUpload = async (file: File) => {
    setUploadingDialog(false);
    setUploading(true);
    const data = await readFile(file);
    if (!data) {
      setUploading(false);
      setUploadedPricessCount(0);
    }
    setUploadedPricessCount(data.split('\n').length - 1);
    api.uploadPrices(data);
    setUploading(false);
  };

  const handlePriceDelete = useCallback(() => {
    setDeleteDialog(false);
    setDeleted(true);
    api.deletePrices(null);
  }, [api]);

  return (
    <>
      <UploadDialog
        open={uploadingDialog}
        confirmationTitle={t('export.prices.uploadConfirmation.title')}
        confirmationBody={t('export.prices.uploadConfirmation.body')}
        onUpload={handlePricesUpload}
        onClose={() => {
          setUploadingDialog(false);
        }}
        metadataParser={async (file: File) => {
          const data = await readFile(file);
          return { Prices: String(data.split('\n').length - 1) };
        }}
      />

      <ConfirmationDialog
        open={deleteDialog}
        title={t('export.prices.deleteConfirmation.title')}
        body={t('export.prices.deleteConfirmation.body')}
        onConfirm={handlePriceDelete}
        onCancel={() => {
          setDeleteDialog(false);
        }}
      />

      <Paper
        legend={
          <Stack direction="row" gap={1} alignItems="center">
            <PricesIcon style={{ fill: 'white' }} />
            <Typography variant="body2">
              {t('export.prices.panelHeadline')}
            </Typography>
          </Stack>
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={handlePricesDownload}
              startIcon={<DownloadIcon />}
              variant="contained"
              loading={downloading}
              loadingPosition="start"
            >
              {t('export.prices.downloadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {downloadedPricesCount !== undefined && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.prices.downloadedHint', {
                      count: downloadedPricesCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={() => {
                setUploadingDialog(true);
              }}
              startIcon={<UploadIcon />}
              variant="contained"
              loading={uploading}
              loadingPosition="start"
            >
              {t('export.prices.uploadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {uploadedPricesCount !== undefined && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.prices.uploadedHint', {
                      count: uploadedPricesCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={() => {
                setDeleteDialog(true);
              }}
              startIcon={<DeleteForeverIcon />}
              variant="outlined"
              color="error"
            >
              {t('export.prices.deleteButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {deleted && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.prices.deletedHint')}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ marginTop: '16px' }}>
          <Typography variant="body2">
            {t('export.prices.noShopsHint')}
          </Typography>
        </Alert>
      </Paper>
    </>
  );
}
